import dayjs from "lib/dayjs";
import {
  computePositionYields,
  priceFromYield,
  yieldFromPrice,
} from "lib/bondMath";
import {
  dayCountFraction,
  dayCountCouponPeriodFraction,
  freqToPeriodsPerYear,
} from "lib/dayCount";
import { buildSlgsNoteCashflows } from "./slgsNoteSchedule";
import { calculatePurchaseCost, roundToCents } from "./money";
import { getPortfolioComparisonPeriod } from "../components/Portfolios/portfolioPeriodDates";

export const CASH_LIKE_TYPES = new Set([
  "bank deposit",
  "cash",
  "lgip",
  "money market fund",
  "money market funds",
  "money market",
  "slgs demand deposit",
  "demand deposit",
]);

export const SLGS_TYPES = new Set(["slgs time deposit", "slgs demand deposit"]);

export const CUSIP_INVESTMENT_TYPES = new Set([
  "us treasury",
  "us agency",
  "us agency - callable",
  "commercial paper",
  "corporate bond",
  "municipal bond",
]);

export function trimToEmpty(value) {
  return String(value ?? "").trim();
}

export function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function toNumberOrZero(value) {
  return toNumberOrNull(value) ?? 0;
}

export function normalizeYieldRate(value) {
  const n = toNumberOrNull(value);
  if (n == null) return null;
  return Math.abs(n) > 1 ? n / 100 : n;
}

export function parseIsoDate(value) {
  const d = dayjs(trimToEmpty(value));
  return d.isValid() ? d : null;
}

export function dateLTE(value, maxDate) {
  const s = trimToEmpty(value);
  return !!s && s <= maxDate;
}

export function dateLT(value, maxDateExclusive) {
  const s = trimToEmpty(value);
  return !!s && s < maxDateExclusive;
}

export function dateInPeriod(value, startDate, endDate) {
  const s = trimToEmpty(value);
  return !!s && s >= startDate && s <= endDate;
}

export function daysBetween(startDate, endDate) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  if (!start || !end) return null;
  return end.diff(start, "day");
}

export function normalizeCouponRate(value) {
  const n = toNumberOrNull(value);
  if (n == null) return null;
  // FundPlanner stores coupon values in percentage points (for example,
  // 4.500 means 4.500% and 0.800 means 0.800%). Keep that data contract
  // explicit here instead of guessing from the value's magnitude.
  return n / 100;
}

export function getEligibleContractualCallDate(
  security = {},
  asOfDate = "",
  maturityDate = ""
) {
  if (!security?.is_callable) return "";

  const firstCallDate = trimToEmpty(security?.first_call_date);
  if (!firstCallDate || !asOfDate || firstCallDate <= asOfDate) return "";
  if (maturityDate && firstCallDate > maturityDate) return "";
  return firstCallDate;
}

export function normalizeFrequency(value) {
  return freqToPeriodsPerYear(value || "semiannual");
}

export function getPositionInvestmentType(position) {
  return trimToEmpty(position?.investment_type).toLowerCase();
}

export function getSecurityText(position, security) {
  return [
    position?.investment_type,
    position?.raw_security_type,
    position?.raw_security_description,
    security?.security_type,
    security?.security_description,
    security?.coupon_type,
  ]
    .map((value) => trimToEmpty(value).toLowerCase())
    .filter(Boolean)
    .join(" ");
}

export function isCashLike(position) {
  return CASH_LIKE_TYPES.has(getPositionInvestmentType(position));
}

export function isCusipInvestmentType(position) {
  return CUSIP_INVESTMENT_TYPES.has(getPositionInvestmentType(position));
}

export function isDiscountSecurity(position, security) {
  const text = getSecurityText(position, security);
  const coupon = toNumberOrNull(position?.raw_coupon ?? security?.coupon);

  return (
    text.includes("bill") ||
    text.includes("zero") ||
    text.includes("discount") ||
    trimToEmpty(security?.coupon_type).toLowerCase() === "zero" ||
    coupon === 0
  );
}

export function classifySecurity(position = {}, security = {}) {
  const type = getPositionInvestmentType(position);
  const text = getSecurityText(position, security);

  if (type === "cash") return "cash";
  if (type === "bank deposit") return "bank_deposit";
  if (type === "cd") return "cd";
  if (type === "lgip") return "lgip";
  if (type.includes("money market")) return "money_market_fund";
  if (type === "slgs demand deposit") return "slgs_demand";
  if (type === "slgs time deposit") return "slgs_time";
  if (type === "bank deposit / cd") return "cd";
  if (type === "commercial paper" || text.includes("commercial paper")) {
    return "discount_security";
  }
  if (type.includes("municipal") || text.includes("municipal")) return "municipal";
  if (type.includes("agency") || text.includes("agency")) return "agency";
  if (type.includes("treasury") || text.includes("treasury")) {
    return isDiscountSecurity(position, security) ? "treasury_bill" : "treasury_note_bond";
  }
  if (isDiscountSecurity(position, security)) return "discount_security";
  if (text.includes("corporate")) return "corporate";
  return "fixed_income";
}

export function isEffectiveYieldSecurityCategory(category) {
  return (
    category === "bank_deposit" ||
    category === "lgip" ||
    category === "money_market_fund" ||
    category === "slgs_demand"
  );
}

export function isEffectiveYieldOverrideCategory(category) {
  return category === "bank_deposit";
}

export function findEffectiveYieldOverride({
  positionId,
  valuations = [],
  asOfDate,
  periodStart = "",
  periodEnd = "",
}) {
  const requiredStart = periodStart || asOfDate;
  const requiredEnd = periodEnd || asOfDate;
  if (!positionId || !requiredStart || !requiredEnd) return null;

  return (
    valuations
      .filter((row) => row?.position_id === positionId)
      .filter((row) => toNumberOrNull(row?.effective_yield_override) != null)
      .filter((row) => dateLTE(row?.valuation_date, asOfDate))
      .filter(
        (row) =>
          trimToEmpty(row?.effective_yield_start_date) <= requiredStart &&
          trimToEmpty(row?.effective_yield_end_date) >= requiredEnd
      )
      .sort((a, b) => {
        const startCompare = trimToEmpty(b?.effective_yield_start_date).localeCompare(
          trimToEmpty(a?.effective_yield_start_date)
        );
        if (startCompare) return startCompare;
        return trimToEmpty(b?.created_at).localeCompare(trimToEmpty(a?.created_at));
      })[0] || null
  );
}

export function isParValuedSecurityCategory(category) {
  return (
    category === "bank_deposit" ||
    category === "cash" ||
    category === "cd" ||
    category === "lgip" ||
    category === "money_market_fund" ||
    category === "slgs_demand" ||
    category === "slgs_time"
  );
}

export function getStaticMaturityDate(position = {}, security = {}) {
  return (
    trimToEmpty(position.raw_maturity_date) ||
    trimToEmpty(position.maturity_date) ||
    trimToEmpty(security.maturity_date)
  );
}

export function getStaticCoupon(position = {}, security = {}) {
  return (
    toNumberOrNull(position.raw_coupon) ??
    toNumberOrNull(position.rate) ??
    toNumberOrNull(security.coupon)
  );
}

export function latestByDate(rows, dateField, asOfDate, predicate = () => true) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => predicate(row) && dateLTE(row?.[dateField], asOfDate))
    .sort((a, b) => {
      const dateCompare = trimToEmpty(b?.[dateField]).localeCompare(
        trimToEmpty(a?.[dateField])
      );
      if (dateCompare) return dateCompare;
      return trimToEmpty(b?.created_at).localeCompare(trimToEmpty(a?.created_at));
    })[0] || null;
}

export function filterRowsByPosition(rows, positionId) {
  return (Array.isArray(rows) ? rows : []).filter(
    (row) => row?.position_id === positionId
  );
}

export function calculateOriginalCost(position, parAmount = null) {
  const explicit = toNumberOrNull(position?.original_cost);
  if (explicit != null) {
    const originalPar = toNumberOrNull(position?.par_amount);
    const currentPar = toNumberOrNull(parAmount);
    if (originalPar && currentPar != null && currentPar !== originalPar) {
      return roundToCents((explicit * currentPar) / originalPar);
    }
    return roundToCents(explicit);
  }

  const par =
    parAmount ??
    toNumberOrNull(position?.par_amount) ??
    toNumberOrNull(position?.current_face) ??
    toNumberOrNull(position?.units);
  const price = toNumberOrNull(position?.purchase_price);

  if (par == null || price == null) return null;
  return calculatePurchaseCost(par, price);
}

export function calculatePositionAmountAsOf({
  position = {},
  activities = [],
  valuations = [],
  asOfDate,
  stateDateExclusive = addDaysIso(asOfDate, 1),
}) {
  const boundaryPostingGroups = interestReinvestmentPostingGroupsAtBoundary({
    positionId: position.id,
    activities,
    asOfDate,
  });
  const valuation = latestByDate(
    valuations,
    "valuation_date",
    asOfDate,
    (row) => row.position_id === position.id
  );
  const initialPositionValuation = latestByDate(
    valuations,
    "valuation_date",
    asOfDate,
    (row) =>
      row.position_id === position.id &&
      row.source_type === "initial_position" &&
      (toNumberOrNull(row.current_face) != null ||
        toNumberOrNull(row.par_amount) != null ||
        toNumberOrNull(row.units) != null ||
        toNumberOrNull(row.balance_amount) != null)
  );
  const amountAnchor = latestByDate(
    valuations,
    "valuation_date",
    asOfDate,
    (row) =>
      row.position_id === position.id &&
      row.source_type !== "initial_position" &&
      (toNumberOrNull(row.current_face) != null ||
        toNumberOrNull(row.par_amount) != null ||
        toNumberOrNull(row.units) != null ||
        toNumberOrNull(row.balance_amount) != null)
  );

  let parAmount =
    toNumberOrNull(amountAnchor?.current_face) ??
    toNumberOrNull(amountAnchor?.par_amount) ??
    toNumberOrNull(initialPositionValuation?.current_face) ??
    toNumberOrNull(initialPositionValuation?.par_amount) ??
    toNumberOrNull(position.par_amount) ??
    toNumberOrNull(position.current_face);
  let units =
    toNumberOrNull(amountAnchor?.units) ??
    toNumberOrNull(amountAnchor?.par_amount) ??
    toNumberOrNull(initialPositionValuation?.units) ??
    toNumberOrNull(initialPositionValuation?.par_amount) ??
    toNumberOrNull(position.par_amount) ??
    toNumberOrNull(position.units);
  let balanceAmount =
    toNumberOrNull(amountAnchor?.balance_amount) ??
    toNumberOrNull(amountAnchor?.units) ??
    toNumberOrNull(amountAnchor?.par_amount) ??
    toNumberOrNull(initialPositionValuation?.balance_amount) ??
    toNumberOrNull(initialPositionValuation?.units) ??
    toNumberOrNull(initialPositionValuation?.par_amount) ??
    toNumberOrNull(position.par_amount) ??
    toNumberOrNull(position.balance_amount);
  balanceAmount = roundToCents(balanceAmount);

  const activityRows = filterRowsByPosition(activities, position.id)
    .filter(
      (row) =>
        dateLT(row.activity_date, stateDateExclusive) ||
        // A linked posting at the next day's opening is part of the prior
        // day's closing state. Other next-day activity remains excluded.
        (trimToEmpty(row.activity_date) === stateDateExclusive &&
          isInterestReinvestmentPosting(row) &&
          boundaryPostingGroups.has(
            interestReinvestmentGroupId(row.external_activity_id)
          ))
    )
    .filter(
      (row) =>
        !amountAnchor?.valuation_date ||
        trimToEmpty(row.activity_date) > trimToEmpty(amountAnchor.valuation_date)
    )
    .sort((a, b) => {
      const dateCompare = trimToEmpty(a.activity_date).localeCompare(
        trimToEmpty(b.activity_date)
      );
      if (dateCompare) return dateCompare;
      const valuationRank = (row) =>
        trimToEmpty(row?.source_type) === "valuation_update" ? 1 : 0;
      const rankCompare = valuationRank(a) - valuationRank(b);
      if (rankCompare) return rankCompare;
      return trimToEmpty(a.created_at).localeCompare(trimToEmpty(b.created_at));
    });

  for (const row of activityRows) {
    const parAfter = toNumberOrNull(row.par_after);
    const unitsAfter = toNumberOrNull(row.units_after);
    const balanceAfter = toNumberOrNull(row.balance_after);

    if (parAfter != null) parAmount = parAfter;
    else if (toNumberOrNull(row.par_delta) != null) {
      parAmount = (parAmount ?? 0) + toNumberOrNull(row.par_delta);
    }

    if (unitsAfter != null) units = unitsAfter;
    else if (toNumberOrNull(row.units_delta) != null) {
      units = (units ?? 0) + toNumberOrNull(row.units_delta);
    }

    if (balanceAfter != null) balanceAmount = roundToCents(balanceAfter);
    else if (toNumberOrNull(row.units_delta) != null && balanceAmount != null) {
      balanceAmount = roundToCents(
        balanceAmount + roundToCents(row.units_delta)
      );
    }
  }

  if (position.closed_date && dateLT(position.closed_date, stateDateExclusive)) {
    parAmount = 0;
    units = 0;
    balanceAmount = 0;
  }

  const currentAmount = isCusipInvestmentType(position)
    ? parAmount
    : balanceAmount ?? units ?? parAmount;

  return {
    parAmount,
    units,
    balanceAmount,
    currentAmount,
    latestValuation: valuation,
    amountAnchor,
  };
}

function amountFromFields(row = {}, fields = []) {
  const source = row || {};
  for (const field of fields) {
    const n = toNumberOrNull(source[field]);
    if (n != null) return n;
  }
  return null;
}

function initialBalanceFromPosition(position = {}, valuations = [], asOfDate) {
  const initialValuation =
    (Array.isArray(valuations) ? valuations : [])
      .filter((row) => row?.position_id === position.id)
      .filter((row) => row?.source_type === "initial_position")
      .filter((row) => dateLTE(row?.valuation_date, asOfDate))
      .sort((a, b) => trimToEmpty(a?.valuation_date).localeCompare(trimToEmpty(b?.valuation_date)))[0] ||
    null;

  const balance =
    amountFromFields(initialValuation, [
      "balance_amount",
      "units",
      "current_face",
      "par_amount",
    ]) ??
    amountFromFields(position, [
      "balance_amount",
      "units",
      "current_face",
      "par_amount",
    ]);

  const date =
    trimToEmpty(position?.settlement_date) ||
    trimToEmpty(initialValuation?.valuation_date) ||
    trimToEmpty(position?.purchase_date);

  if (balance == null || !date || date > asOfDate) return null;
  return { date, amount: balance };
}

function activityAmount(row = {}) {
  return amountFromFields(row, [
    "units_delta",
    "par_delta",
    "cash_amount",
    "income_amount",
  ]);
}

function interestIncomeAmount(row = {}) {
  return amountFromFields(row, ["income_amount", "cash_amount"]);
}

function interestReinvestmentGroupId(value) {
  return trimToEmpty(value).replace(/:(?:income|posting)$/, "");
}

function isInterestReinvestmentActivity(row = {}) {
  return [
    "manual_interest_reinvestment",
    "system_scheduled_interest_reinvestment",
  ].includes(trimToEmpty(row?.source_type));
}

function isInterestReinvestmentPosting(row = {}) {
  return row?.activity_type === "deposit" && isInterestReinvestmentActivity(row);
}

function interestReinvestmentPostingGroupsAtBoundary({
  positionId,
  activities = [],
  asOfDate,
} = {}) {
  const boundaryDate = addDaysIso(asOfDate, 1);
  if (!positionId || !boundaryDate) return new Set();

  const positionRows = filterRowsByPosition(activities, positionId);
  const earnedGroups = new Set(
    positionRows
      .filter((row) => row?.activity_type === "interest_income")
      .filter((row) => isInterestReinvestmentActivity(row))
      .filter((row) => dateLTE(row?.activity_date, asOfDate))
      .map((row) => interestReinvestmentGroupId(row?.external_activity_id))
      .filter(Boolean)
  );

  return new Set(
    positionRows
      .filter((row) => isInterestReinvestmentPosting(row))
      .filter((row) => trimToEmpty(row?.activity_date) === boundaryDate)
      .map((row) => interestReinvestmentGroupId(row?.external_activity_id))
      .filter((groupId) => groupId && earnedGroups.has(groupId))
  );
}

export function calculateEarnedIncomeNotCredited({
  position = {},
  activities = [],
  asOfDate,
} = {}) {
  if (!position?.id || !asOfDate) return 0;

  const positionRows = filterRowsByPosition(activities, position.id);
  const boundaryPostingGroups = interestReinvestmentPostingGroupsAtBoundary({
    positionId: position.id,
    activities,
    asOfDate,
  });
  const rows = positionRows.filter((row) => dateLTE(row?.activity_date, asOfDate));
  const creditedGroups = new Set(
    positionRows
      .filter((row) => isInterestReinvestmentPosting(row))
      .filter(
        (row) =>
          dateLTE(row?.activity_date, asOfDate) ||
          boundaryPostingGroups.has(
            interestReinvestmentGroupId(row?.external_activity_id)
          )
      )
      .map((row) => interestReinvestmentGroupId(row?.external_activity_id))
      .filter(Boolean)
  );

  return rows
    .filter((row) => row?.activity_type === "interest_income")
    .filter((row) => isInterestReinvestmentActivity(row))
    .filter((row) => {
      const groupId = interestReinvestmentGroupId(row?.external_activity_id);
      return groupId && !creditedGroups.has(groupId);
    })
    .reduce(
      (sum, row) => sum + Math.abs(toNumberOrZero(interestIncomeAmount(row))),
      0
    );
}

export function addDaysIso(dateText, days) {
  const date = parseIsoDate(dateText);
  return date ? date.add(days, "day").format("YYYY-MM-DD") : "";
}

function daysInclusive(startDate, endDate) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  if (!start || !end || end.isBefore(start, "day")) return 0;
  return end.diff(start, "day") + 1;
}

function hasSameDayInitialCapitalActivity(activityRows, initial) {
  if (!initial) return false;
  const initialAmount = Math.abs(toNumberOrZero(initial.amount));
  if (initialAmount <= 0) return false;

  const sameDayTotal = activityRows
    .filter((row) => trimToEmpty(row?.activity_date) === initial.date)
    .filter((row) => ["deposit", "purchase"].includes(row?.activity_type))
    .filter((row) => row?.source_type !== "position_purchase_funding")
    .reduce((sum, row) => sum + Math.abs(toNumberOrZero(activityAmount(row))), 0);

  return sameDayTotal > 0 && Math.abs(sameDayTotal - initialAmount) < 0.005;
}

function buildEffectiveYieldEvents(activityRows, initial) {
  const events = [];

  if (initial && !hasSameDayInitialCapitalActivity(activityRows, initial)) {
    events.push({ date: initial.date, amount: initial.amount });
  }

  for (const row of activityRows) {
    const type = trimToEmpty(row?.activity_type);
    const date = trimToEmpty(row?.activity_date);
    if (!date) continue;

    if (type === "interest_income") {
      const reinvestAmount = amountFromFields(row, ["units_delta", "par_delta"]);
      const reinvestDate = addDaysIso(date, 1);
      if (reinvestAmount != null && reinvestAmount > 0) {
        events.push({ date: reinvestDate, amount: reinvestAmount });
      }
      continue;
    }

    if (
      type === "purchase" &&
      row?.source_type === "position_purchase_funding"
    ) {
      const amount = activityAmount(row);
      if (amount != null) events.push({ date, amount: -Math.abs(amount) });
      continue;
    }

    if (["deposit", "purchase", "transfer_in"].includes(type)) {
      const amount = activityAmount(row);
      if (amount != null) events.push({ date, amount: Math.abs(amount) });
      continue;
    }

    if (
      [
        "withdrawal",
        "fee_expense",
        "sale",
        "partial_sale",
        "redemption",
        "principal_paydown",
        "transfer_out",
      ].includes(type)
    ) {
      const amount = activityAmount(row);
      if (amount != null) events.push({ date, amount: -Math.abs(amount) });
    }
  }

  return events;
}

function balanceAsOfDate(events = [], asOfDate) {
  return events
    .filter((event) => event.date && event.date <= asOfDate)
    .reduce((sum, event) => sum + event.amount, 0);
}

function calculateDollarDays(events = [], asOfDate) {
  const datedEvents = events
    .filter((event) => event.date && event.date <= asOfDate)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (!datedEvents.length) return 0;

  let currentDate = datedEvents[0].date;
  let currentBalance = 0;
  let totalDollarDays = 0;
  let index = 0;

  while (index < datedEvents.length) {
    const eventDate = datedEvents[index].date;
    const priorDate = addDaysIso(eventDate, -1);
    totalDollarDays += currentBalance * daysInclusive(currentDate, priorDate);

    while (index < datedEvents.length && datedEvents[index].date === eventDate) {
      currentBalance += datedEvents[index].amount;
      index += 1;
    }

    currentDate = eventDate;
  }

  totalDollarDays += currentBalance * daysInclusive(currentDate, asOfDate);
  return totalDollarDays;
}

function calculateDollarDaysInPeriod(events = [], startDate, endDate) {
  const earningStartDate = addDaysIso(startDate, 1);
  if (!earningStartDate || !endDate || earningStartDate > endDate) return 0;

  return calculateDollarDaysInInclusiveRange(events, earningStartDate, endDate);
}

function calculateDollarDaysInInclusiveRange(events = [], startDate, endDate) {
  if (!startDate || !endDate || startDate > endDate) return 0;

  const priorDate = addDaysIso(startDate, -1);
  const beginningBalance = priorDate ? balanceAsOfDate(events, priorDate) : 0;
  const periodEvents = [
    { date: startDate, amount: beginningBalance },
    ...events.filter((event) => event.date >= startDate && event.date <= endDate),
  ];

  return calculateDollarDays(periodEvents, endDate);
}

function firstEventDate(events = []) {
  return events
    .filter((event) => event.date)
    .sort((a, b) => a.date.localeCompare(b.date))[0]?.date;
}

function overlapInclusiveRange(aStart, aEnd, bStart, bEnd) {
  const start = aStart > bStart ? aStart : bStart;
  const end = aEnd < bEnd ? aEnd : bEnd;
  return start && end && start <= end ? { start, end } : null;
}

function interestIncomePeriod(row, previousInterestRow, initial, events) {
  const explicitStart = trimToEmpty(row?.income_period_start);
  const explicitEnd = trimToEmpty(row?.income_period_end);
  if (explicitStart && explicitEnd && explicitStart <= explicitEnd) {
    return { start: explicitStart, end: explicitEnd };
  }

  const end = trimToEmpty(row?.activity_date);
  const inferredStart = previousInterestRow
    ? addDaysIso(trimToEmpty(previousInterestRow.activity_date), 1)
    : initial?.date || firstEventDate(events);
  const start = previousInterestRow ? inferredStart : addDaysIso(inferredStart, 1);

  return start && end && start <= end ? { start, end } : null;
}

function buildObservedInterestYieldSegments({
  events = [],
  interestRows = [],
  initial = null,
}) {
  const segments = [];
  const sortedInterestRows = [...interestRows].sort((a, b) =>
    trimToEmpty(a?.activity_date).localeCompare(trimToEmpty(b?.activity_date))
  );

  for (let i = 0; i < sortedInterestRows.length; i += 1) {
    const row = sortedInterestRows[i];
    const amount = Math.abs(interestIncomeAmount(row));
    const incomePeriod = interestIncomePeriod(
      row,
      sortedInterestRows[i - 1] || null,
      initial,
      events
    );
    if (!incomePeriod || amount <= 0) continue;

    const dollarDays = calculateDollarDaysInInclusiveRange(
      events,
      incomePeriod.start,
      incomePeriod.end
    );
    if (dollarDays <= 0) continue;

    segments.push({
      startDate: incomePeriod.start,
      endDate: incomePeriod.end,
      activityDate: trimToEmpty(row?.activity_date),
      interestAmount: amount,
      dollarDays,
      yieldRate: (amount * 365) / dollarDays,
    });
  }

  return segments;
}

function estimateInterestForRange(events, startDate, endDate, yieldRate) {
  if (yieldRate == null || startDate > endDate) return 0;
  const dollarDays = calculateDollarDaysInInclusiveRange(events, startDate, endDate);
  return (dollarDays * yieldRate) / 365;
}

function estimateInterestForPeriod({
  events = [],
  observedSegments = [],
  startDate,
  endDate,
}) {
  let cursor = startDate;
  let estimatedInterest = 0;
  let estimatedUnpostedInterest = 0;
  let lastYieldRate = null;

  for (const segment of observedSegments) {
    if (segment.endDate < startDate) {
      lastYieldRate = segment.yieldRate;
      continue;
    }
    if (segment.startDate > endDate) break;

    const gapEnd = addDaysIso(segment.startDate, -1);
    if (cursor <= gapEnd && lastYieldRate != null) {
      const gapInterest = estimateInterestForRange(
        events,
        cursor,
        gapEnd,
        lastYieldRate
      );
      estimatedInterest += gapInterest;
      estimatedUnpostedInterest += gapInterest;
    }

    const overlap = overlapInclusiveRange(
      segment.startDate,
      segment.endDate,
      startDate,
      endDate
    );
    if (overlap) {
      estimatedInterest += estimateInterestForRange(
        events,
        overlap.start,
        overlap.end,
        segment.yieldRate
      );
      cursor = addDaysIso(overlap.end, 1);
    }

    lastYieldRate = segment.yieldRate;
  }

  if (cursor <= endDate && lastYieldRate != null) {
    const trailingInterest = estimateInterestForRange(
      events,
      cursor,
      endDate,
      lastYieldRate
    );
    estimatedInterest += trailingInterest;
    estimatedUnpostedInterest += trailingInterest;
  }

  return { estimatedInterest, estimatedUnpostedInterest };
}

function calculateEffectiveYieldForPeriod({
  events = [],
  interestRows = [],
  initial = null,
  startDate,
  endDate,
}) {
  if (!startDate || !endDate || startDate >= endDate) return null;

  const earningStartDate = addDaysIso(startDate, 1);
  if (!earningStartDate || earningStartDate > endDate) return null;

  const periodInterestRows = interestRows.filter((row) =>
    dateInPeriod(row?.activity_date, startDate, endDate)
  );
  const totalPostedInterest = periodInterestRows.reduce(
    (sum, row) => sum + Math.abs(interestIncomeAmount(row)),
    0
  );
  const totalDollarDays = calculateDollarDaysInPeriod(events, startDate, endDate);
  const observedSegments = buildObservedInterestYieldSegments({
    events,
    interestRows,
    initial,
  });
  const { estimatedInterest, estimatedUnpostedInterest } = estimateInterestForPeriod({
    events,
    observedSegments,
    startDate: earningStartDate,
    endDate,
  });

  if (estimatedInterest <= 0 || totalDollarDays <= 0) {
    return {
      effectiveYield: null,
      accruedInterest: null,
      totalPostedInterest,
      totalDollarDays,
    };
  }

  return {
    effectiveYield: (estimatedInterest * 365) / totalDollarDays,
    accruedInterest: estimatedUnpostedInterest,
    totalPostedInterest,
    totalDollarDays,
  };
}

export function calculateEffectiveYieldComponentsFromActivity({
  position = {},
  activities = [],
  valuations = [],
  asOfDate,
  startDate = "",
  endDate = "",
}) {
  if (!asOfDate) {
    return {
      effectiveYield: null,
      accruedInterest: null,
      totalPostedInterest: 0,
      totalDollarDays: 0,
    };
  }

  const activityRows = filterRowsByPosition(activities, position.id)
    .filter((row) => dateLTE(row?.activity_date, asOfDate))
    .sort((a, b) => trimToEmpty(a?.activity_date).localeCompare(trimToEmpty(b?.activity_date)));

  const initial = initialBalanceFromPosition(position, valuations, asOfDate);
  const events = buildEffectiveYieldEvents(activityRows, initial);
  const interestRows = activityRows
    .filter((row) => row?.activity_type === "interest_income")
    .filter((row) => interestIncomeAmount(row) != null);
  const earnedIncomeNotCredited = calculateEarnedIncomeNotCredited({
    position,
    activities,
    asOfDate,
  });

  if (startDate && endDate) {
    const periodResult = calculateEffectiveYieldForPeriod({
      events,
      interestRows,
      initial,
      startDate,
      endDate,
    });
    return {
      ...periodResult,
      accruedInterest:
        toNumberOrZero(periodResult.accruedInterest) + earnedIncomeNotCredited,
      earnedIncomeNotCredited,
    };
  }

  if (!interestRows.length) {
    return {
      effectiveYield: null,
      accruedInterest: null,
      totalPostedInterest: 0,
      totalDollarDays: calculateDollarDays(events, asOfDate),
    };
  }

  const totalPostedInterest = interestRows.reduce(
    (sum, row) => sum + Math.abs(interestIncomeAmount(row)),
    0
  );
  const totalDollarDays = calculateDollarDays(events, asOfDate);

  if (totalDollarDays <= 0) {
    return {
      effectiveYield: null,
      accruedInterest: null,
      totalPostedInterest,
      totalDollarDays,
    };
  }

  const lastInterest = interestRows[interestRows.length - 1];
  const lastInterestDate = trimToEmpty(lastInterest?.activity_date);
  const previousInterest = interestRows[interestRows.length - 2] || null;
  const firstEventDate = events
    .filter((event) => event.date)
    .sort((a, b) => a.date.localeCompare(b.date))[0]?.date;
  const periodStart = previousInterest
    ? addDaysIso(trimToEmpty(previousInterest.activity_date), 1)
    : initial?.date || firstEventDate;
  const periodEnd = lastInterestDate;
  const periodDays = daysInclusive(periodStart, periodEnd);
  const periodBeginningBalance = balanceAsOfDate(events, periodStart);
  const lastInterestAmount = Math.abs(interestIncomeAmount(lastInterest));

  let accruedInterest = 0;
  if (
    asOfDate > lastInterestDate &&
    periodDays > 0 &&
    periodBeginningBalance > 0 &&
    lastInterestAmount > 0
  ) {
    const impliedYield =
      (lastInterestAmount / periodBeginningBalance) * (365 / periodDays);
    const accruedStart = addDaysIso(lastInterestDate, 1);
    const accruedDays = daysInclusive(accruedStart, asOfDate);
    const currentInvestedBalance = balanceAsOfDate(events, asOfDate);

    accruedInterest =
      currentInvestedBalance > 0 && accruedDays > 0
        ? currentInvestedBalance * impliedYield * (accruedDays / 365)
        : 0;
  }

  return {
    effectiveYield:
      ((totalPostedInterest + accruedInterest) * 365) / totalDollarDays,
    accruedInterest: accruedInterest + earnedIncomeNotCredited,
    earnedIncomeNotCredited,
    totalPostedInterest,
    totalDollarDays,
  };
}

export function calculateEffectiveYieldFromActivity(args) {
  return calculateEffectiveYieldComponentsFromActivity(args).effectiveYield;
}

export function calculatePositionBookValue({
  position = {},
  security = {},
  valuation = null,
  asOfDate,
  parAmount = null,
}) {
  const category = classifySecurity(position, security);

  // Stable-value positions are carried at their date-effective balance. An
  // absolute value stored on an earlier valuation (including the initial
  // position row) must not override later deposits, withdrawals, or balance
  // corrections.
  if (isParValuedSecurityCategory(category) || category === "slgs_demand") {
    return parAmount ?? toNumberOrNull(position.par_amount) ?? 0;
  }

  const override = toNumberOrNull(valuation?.book_value);
  if (override != null) return override;

  const constantYieldBookValue = calculateConstantYieldBookValue({
    position,
    security,
    asOfDate,
    parAmount,
  });
  if (constantYieldBookValue != null) return constantYieldBookValue;

  const par = parAmount ?? toNumberOrNull(position.par_amount);
  const originalCost = calculateOriginalCost(position, par);
  const settlementDate = trimToEmpty(position.settlement_date || position.purchase_date);
  const maturityDate = getStaticMaturityDate(position, security);
  const totalDays = daysBetween(settlementDate, maturityDate);
  const elapsedDays = daysBetween(settlementDate, asOfDate);

  if (par == null || originalCost == null) return null;
  if (!totalDays || totalDays <= 0 || elapsedDays == null) return originalCost;

  const progress = Math.max(0, Math.min(1, elapsedDays / totalDays));
  return originalCost + (par - originalCost) * progress;
}

function couponDatesAroundDate(maturityDate, frequency, targetDate) {
  const maturity = parseIsoDate(maturityDate);
  const target = parseIsoDate(targetDate);
  if (!maturity || !target) return null;

  const periods = normalizeFrequency(frequency);
  const months = 12 / periods;
  let nextCoupon = maturity;

  for (let i = 0; i < 600; i += 1) {
    const previousCoupon = nextCoupon.subtract(months, "month");
    if (!previousCoupon.isAfter(target, "day")) {
      return { previousCoupon, nextCoupon };
    }
    nextCoupon = previousCoupon;
  }

  return null;
}

function cleanPriceAtCouponBoundary({
  couponRate,
  yieldRate,
  couponDate,
  maturityDate,
  dayCount,
  frequency,
}) {
  if (couponDate === maturityDate) return 100;

  return priceFromYield({
    couponRate,
    yieldRate,
    deliveryDate: couponDate,
    maturityDate,
    dayCount,
    frequency,
    redemption: 100,
    applyParSnap: false,
  });
}

const constantYieldByTerms = new Map();

function acquisitionYieldForTerms({
  couponRate,
  acquisitionCleanPrice,
  settlementDate,
  maturityDate,
  datedDate,
  dayCount,
  frequency,
}) {
  const key = [
    couponRate,
    acquisitionCleanPrice,
    settlementDate,
    maturityDate,
    datedDate,
    dayCount,
    frequency,
  ].join("|");
  if (constantYieldByTerms.has(key)) return constantYieldByTerms.get(key);

  let yieldRate = null;
  try {
    yieldRate = yieldFromPrice({
      couponRate,
      priceTarget: acquisitionCleanPrice,
      deliveryDate: settlementDate,
      maturityDate,
      datedDate,
      dayCount,
      frequency,
      redemption: 100,
      tol: 1e-12,
    });
  } catch {
    yieldRate = null;
  }

  if (constantYieldByTerms.size >= 2000) constantYieldByTerms.clear();
  constantYieldByTerms.set(key, yieldRate);
  return yieldRate;
}

export function calculateConstantYieldBookValue({
  position = {},
  security = {},
  asOfDate,
  parAmount = null,
}) {
  if (!isCusipInvestmentType(position) || security?.is_callable) return null;

  const couponType = trimToEmpty(security?.coupon_type).toUpperCase();
  if (["ADJUSTABLE", "FLOATING", "VARIABLE"].includes(couponType)) return null;
  if (isDiscountSecurity(position, security)) return null;

  const settlementDate = trimToEmpty(
    position?.settlement_date || position?.purchase_date
  );
  const maturityDate = getStaticMaturityDate(position, security);
  const settlement = parseIsoDate(settlementDate);
  const maturity = parseIsoDate(maturityDate);
  const calculationDate = parseIsoDate(asOfDate);
  const originalPar =
    toNumberOrNull(position?.par_amount) ??
    toNumberOrNull(position?.current_face);
  const currentPar = parAmount ?? originalPar;
  const originalCost = calculateOriginalCost(position, originalPar);
  const couponRate = normalizeCouponRate(getStaticCoupon(position, security));
  const frequency = security?.payment_frequency || "semiannual";
  const periods = normalizeFrequency(frequency);
  const dayCount = getCouponDayCountBasis(
    classifySecurity(position, security),
    security
  );
  const datedDate = trimToEmpty(security?.dated_date) || null;

  if (
    !settlement ||
    !maturity ||
    !calculationDate ||
    !maturity.isAfter(settlement, "day") ||
    originalPar == null ||
    originalPar <= 0 ||
    currentPar == null ||
    originalCost == null ||
    originalCost <= 0 ||
    couponRate == null ||
    !Number.isFinite(periods) ||
    periods <= 0
  ) {
    return null;
  }

  const acquisitionCleanPrice = (originalCost / originalPar) * 100;
  if (Math.abs(acquisitionCleanPrice - 100) < 1e-10) return currentPar;
  if (!calculationDate.isAfter(settlement, "day")) {
    return (currentPar * acquisitionCleanPrice) / 100;
  }
  if (!calculationDate.isBefore(maturity, "day")) return currentPar;

  const yieldRate = acquisitionYieldForTerms({
    couponRate,
    acquisitionCleanPrice,
    settlementDate,
    maturityDate,
    datedDate,
    dayCount,
    frequency,
  });
  if (!Number.isFinite(yieldRate)) return null;

  const frame = couponDatesAroundDate(maturityDate, frequency, asOfDate);
  if (!frame) return null;
  const previousCouponDate = frame.previousCoupon.format("YYYY-MM-DD");
  const nextCouponDate = frame.nextCoupon.format("YYYY-MM-DD");

  let segmentStartDate = previousCouponDate;
  let segmentBeginningPrice = cleanPriceAtCouponBoundary({
    couponRate,
    yieldRate,
    couponDate: previousCouponDate,
    maturityDate,
    dayCount,
    frequency,
  });

  if (settlement.isAfter(frame.previousCoupon, "day")) {
    segmentStartDate = settlementDate;
    segmentBeginningPrice = acquisitionCleanPrice;
  }

  const segmentEndingPrice = cleanPriceAtCouponBoundary({
    couponRate,
    yieldRate,
    couponDate: nextCouponDate,
    maturityDate,
    dayCount,
    frequency,
  });
  const elapsedFraction = dayCountCouponPeriodFraction({
    start: segmentStartDate,
    end: asOfDate,
    nextCoupon: nextCouponDate,
    method: dayCount,
    frequency,
  });
  const fullSegmentFraction = dayCountCouponPeriodFraction({
    start: segmentStartDate,
    end: nextCouponDate,
    nextCoupon: nextCouponDate,
    method: dayCount,
    frequency,
  });
  if (fullSegmentFraction <= 0) return null;

  const recognizedShare = Math.max(
    0,
    Math.min(1, elapsedFraction / fullSegmentFraction)
  );
  const bookPrice =
    segmentBeginningPrice +
    (segmentEndingPrice - segmentBeginningPrice) * recognizedShare;

  return (currentPar * bookPrice) / 100;
}

function couponFrame(
  asOfDate,
  maturityDate,
  frequency,
  treatCouponDateAsPrePayment = false,
  firstInterestPaymentDate = ""
) {
  const asOf = parseIsoDate(asOfDate);
  const maturity = parseIsoDate(maturityDate);
  if (!asOf || !maturity) return null;

  const periods = normalizeFrequency(frequency);
  const months = 12 / periods;
  const firstInterest = parseIsoDate(firstInterestPaymentDate);

  if (firstInterest && !firstInterest.isAfter(maturity, "day")) {
    let prev = firstInterest.subtract(months, "month");
    let next = firstInterest;

    for (let i = 0; i < 600; i += 1) {
      const couponIsPast = next.isBefore(asOf, "day");
      const couponIsToday = next.isSame(asOf, "day");
      if (!couponIsPast && (!couponIsToday || treatCouponDateAsPrePayment)) {
        return { prevCoupon: prev, nextCoupon: next };
      }

      prev = next;
      const scheduledNext = next.add(months, "month");
      if (scheduledNext.isAfter(maturity, "day")) {
        if (prev.isBefore(maturity, "day")) {
          next = maturity;
        } else {
          return null;
        }
      } else {
        next = scheduledNext;
      }
    }

    return null;
  }

  let next = maturity;

  for (let i = 0; i < 600 && next.isAfter(asOf, "day"); i += 1) {
    const prev = next.subtract(months, "month");
    if (!prev.isAfter(asOf, "day")) {
      if (treatCouponDateAsPrePayment && prev.isSame(asOf, "day")) {
        return {
          prevCoupon: prev.subtract(months, "month"),
          nextCoupon: prev,
        };
      }
      return { prevCoupon: prev, nextCoupon: next };
    }
    next = prev;
  }

  if (treatCouponDateAsPrePayment && next.isSame(asOf, "day")) {
    return {
      prevCoupon: next.subtract(months, "month"),
      nextCoupon: next,
    };
  }

  return null;
}

function getCouponAccrualStartDate(
  security = {},
  previousCouponDate,
  couponDate,
  settlementDate = ""
) {
  const datedDate = trimToEmpty(security?.dated_date);
  const firstInterestPaymentDate = trimToEmpty(
    security?.first_interest_payment_date
  );
  if (firstInterestPaymentDate === couponDate) {
    return datedDate || trimToEmpty(settlementDate) || previousCouponDate;
  }

  if (
    datedDate &&
    datedDate > previousCouponDate &&
    datedDate < couponDate
  ) {
    return datedDate;
  }

  return previousCouponDate;
}

function getCouponDayCountBasis(category, security = {}) {
  return category === "treasury_note_bond"
    ? "act/act-ust"
    : trimToEmpty(security.day_count_basis) || "30/360";
}

function calculateSlgsTimeAccruedInterest({
  position,
  security,
  asOfDate,
  par,
  coupon,
}) {
  const settlementDate = trimToEmpty(
    position?.settlement_date || position?.purchase_date
  );
  const maturityDate = getStaticMaturityDate(position, security);
  const settlement = parseIsoDate(settlementDate);
  const maturity = parseIsoDate(maturityDate);
  const asOf = parseIsoDate(asOfDate);

  if (!settlement || !maturity || !asOf) return null;
  if (asOf.isBefore(settlement, "day")) return 0;

  const accrualEnd = asOf.isAfter(maturity, "day") ? maturity : asOf;
  const certificateCutoff = settlement.add(1, "year");

  // SLGS certificates pay all simple interest at maturity.
  if (!maturity.isAfter(certificateCutoff, "day")) {
    return (
      par *
      coupon *
      dayCountFraction(settlement, accrualEnd, "act/act")
    );
  }

  const { regular } = buildSlgsNoteCashflows({
    settlement: settlementDate,
    maturity: maturityDate,
    principal: par,
    ratePct: coupon * 100,
    descriptionBase: trimToEmpty(position?.cusip_or_id) || "SLGS",
  });
  const paymentDates = regular
    .map((flow) => parseIsoDate(flow?.date))
    .filter(Boolean);

  // Interest paid on a report date is no longer accrued on that date.
  if (paymentDates.some((date) => date.isSame(accrualEnd, "day"))) return 0;

  const lastPayment = paymentDates
    .filter((date) => date.isBefore(accrualEnd, "day"))
    .sort((a, b) => b.valueOf() - a.valueOf())[0];
  const accrualStart = lastPayment || settlement;
  const nextPayment = paymentDates.find((date) =>
    date.isAfter(accrualEnd, "day")
  );

  if (!nextPayment) return 0;

  const fullPeriodFraction = dayCountFraction(
    accrualStart,
    nextPayment,
    "act/act-ust"
  );
  const remainingPeriodFraction = dayCountFraction(
    accrualEnd,
    nextPayment,
    "act/act-ust"
  );

  return (
    par *
    coupon *
    Math.max(0, fullPeriodFraction - remainingPeriodFraction)
  );
}

export function calculateAccruedInterest({
  position = {},
  security = {},
  valuation = null,
  asOfDate,
  parAmount = null,
  activities = [],
  treatCouponDateAsPrePayment = false,
}) {
  const override = toNumberOrNull(valuation?.accrued_interest);
  if (override != null) return override;

  const settlementDate = trimToEmpty(
    position?.settlement_date || position?.purchase_date
  );
  if (settlementDate && asOfDate < settlementDate) return 0;

  if (isCashLike(position) || isDiscountSecurity(position, security)) return 0;

  const par = parAmount ?? toNumberOrNull(position.par_amount);
  const coupon = normalizeCouponRate(getStaticCoupon(position, security));
  const maturityDate = getStaticMaturityDate(position, security);
  const frequency = security?.payment_frequency || "semiannual";
  const frame = couponFrame(
    asOfDate,
    maturityDate,
    frequency,
    treatCouponDateAsPrePayment,
    security?.first_interest_payment_date
  );

  if (par == null || coupon == null) return null;

  const category = classifySecurity(position, security);
  if (category === "cd") {
    const accrualEnd = maturityDate && asOfDate > maturityDate ? maturityDate : asOfDate;
    const lastInterestDate = filterRowsByPosition(activities, position.id)
      .filter((row) => row?.activity_type === "interest_income")
      .filter((row) => dateLTE(row?.activity_date, accrualEnd))
      .map((row) => trimToEmpty(row.activity_date))
      .sort()
      .pop();
    const accrualStart = lastInterestDate || settlementDate;
    if (!accrualStart || accrualEnd <= accrualStart) return 0;
    return (
      par *
      coupon *
      dayCountFraction(
        accrualStart,
        accrualEnd,
        normalizeCdDayCountBasis(position.day_count_basis)
      )
    );
  }

  if (category === "slgs_time") {
    return calculateSlgsTimeAccruedInterest({
      position,
      security,
      asOfDate,
      par,
      coupon,
    });
  }

  if (!frame) return null;

  const dayCount = getCouponDayCountBasis(category, security);
  const prevCoupon = frame.prevCoupon.format("YYYY-MM-DD");
  const nextCoupon = frame.nextCoupon.format("YYYY-MM-DD");
  const accrualStart = getCouponAccrualStartDate(
    security,
    prevCoupon,
    nextCoupon,
    settlementDate
  );
  const accrualFraction = dayCountCouponPeriodFraction({
    start: accrualStart,
    end: asOfDate,
    nextCoupon,
    method: dayCount,
    frequency,
  });

  return par * coupon * accrualFraction;
}

export function calculateMarketValue({
  position = {},
  security = {},
  valuation = null,
  securityValuation = null,
  parAmount = null,
}) {
  const par = parAmount ?? toNumberOrNull(position.par_amount);
  const category = classifySecurity(position, security);

  // Stable-value positions are carried at their current date-effective
  // principal. Historical absolute-value fields describe the balance on their
  // own date; they are not enduring overrides after later balance activity.
  if (
    isParValuedSecurityCategory(category) ||
    category === "slgs_time" ||
    category === "slgs_demand"
  ) {
    return par;
  }

  const override = toNumberOrNull(valuation?.market_value);
  if (override != null) return override;

  let price = null;
  if (isParValuedSecurityCategory(category)) {
    price = 100;
  } else if (isCusipInvestmentType(position)) {
    price =
      toNumberOrNull(valuation?.price) ??
      toNumberOrNull(securityValuation?.price_mid);
  } else {
    price =
      toNumberOrNull(valuation?.price) ??
      toNumberOrNull(securityValuation?.price_mid) ??
      toNumberOrNull(position.market_value_input);
  }

  if (par == null || price == null) return null;
  return (par * price) / 100;
}

export function calculateAmortizationAccretion({
  position = {},
  security = {},
  startDate,
  endDate,
  parAmount = null,
}) {
  const startBook = calculatePositionBookValue({
    position,
    security,
    asOfDate: startDate,
    parAmount,
  });
  const endBook = calculatePositionBookValue({
    position,
    security,
    asOfDate: endDate,
    parAmount,
  });

  if (startBook == null || endBook == null) return null;
  return endBook - startBook;
}

export function buildPortfolioSnapshot({
  asOfDate,
  calculationAsOfDate = asOfDate,
  stateDateExclusive = addDaysIso(asOfDate, 1),
  yieldStartDate = "",
  yieldEndDate = "",
  portfolios = [],
  funds = [],
  positions = [],
  activities = [],
  valuations = [],
  securities = [],
  securityValuations = [],
}) {
  const securityByCusip = new Map(
    securities.map((row) => [trimToEmpty(row.cusip).toUpperCase(), row])
  );
  const securityValuationById = new Map();

  for (const row of securityValuations) {
    if (!dateLTE(row.valuation_date, asOfDate)) continue;
    const key = row.security_master_id;
    if (!key || securityValuationById.has(key)) continue;
    securityValuationById.set(key, row);
  }

  const portfolioById = new Map(portfolios.map((row) => [row.id, row]));
  const fundById = new Map(funds.map((row) => [row.id, row]));
  const rows = [];

  for (const position of positions) {
    if (position.purchase_date && !dateLT(position.purchase_date, stateDateExclusive)) continue;

    const settlementDate = trimToEmpty(
      position.settlement_date || position.purchase_date
    );
    const isPendingSettlement = Boolean(
      settlementDate && asOfDate && settlementDate > asOfDate
    );

    const security =
      securityByCusip.get(trimToEmpty(position.cusip_or_id).toUpperCase()) || null;
    const maturityDate = getStaticMaturityDate(position, security || {});
    if (position.closed_date && dateLT(position.closed_date, stateDateExclusive)) continue;
    if (maturityDate && dateLT(maturityDate, stateDateExclusive)) continue;

    const fund = fundById.get(position.fund_id) || null;
    const portfolio = fund ? portfolioById.get(fund.portfolio_id) || null : null;
    const amount = calculatePositionAmountAsOf({
      position,
      activities,
      valuations,
      asOfDate,
      stateDateExclusive,
    });
    const ratingValuation = latestByDate(
      valuations,
      "valuation_date",
      asOfDate,
      (row) =>
        row.position_id === position.id && trimToEmpty(row.rating_rollup)
    );
    const securityValuation = security
      ? securityValuationById.get(security.id) || null
      : null;
    const category = classifySecurity(position, security || {});
    const effectiveYieldComponents = isEffectiveYieldSecurityCategory(category)
      ? calculateEffectiveYieldComponentsFromActivity({
          position,
          activities,
          valuations,
          asOfDate,
          startDate: yieldStartDate,
          endDate: yieldEndDate,
        })
      : null;
    const accruedInterestComponents =
      isEffectiveYieldSecurityCategory(category) && yieldStartDate && yieldEndDate
        ? calculateEffectiveYieldComponentsFromActivity({
            position,
            activities,
            valuations,
            asOfDate,
          })
        : effectiveYieldComponents;
    const effectiveYieldOverride = isEffectiveYieldOverrideCategory(category)
      ? findEffectiveYieldOverride({
          positionId: position.id,
          valuations,
          asOfDate,
          periodStart: yieldStartDate,
          periodEnd: yieldEndDate,
        })
      : null;
    const calculatedEffectiveYield = effectiveYieldComponents?.effectiveYield ?? null;
    const effectiveYield =
      toNumberOrNull(effectiveYieldOverride?.effective_yield_override) ??
      calculatedEffectiveYield;
    const coupon = getStaticCoupon(position, security || {});
    // Call assumptions must come from durable contractual security terms, not
    // Bloomberg's valuation-date `next_call_date`. Until FundPlanner stores a
    // complete call schedule, only an unexpired contractual first call can be
    // evaluated; after that date the safe purchase-yield fallback is YTM.
    const nextCallDate = getEligibleContractualCallDate(
      security || {},
      calculationAsOfDate,
      maturityDate
    );
    const effectiveMaturityDate =
      category === "cash"
        ? calculationAsOfDate
        : isCashLike(position)
        ? dayjs(calculationAsOfDate).add(1, "day").format("YYYY-MM-DD")
        : Boolean(security?.is_callable) && nextCallDate && maturityDate && nextCallDate <= maturityDate
        ? nextCallDate
        : maturityDate;

    const bookValue = calculatePositionBookValue({
      position,
      security: security || {},
      valuation: amount.latestValuation,
      asOfDate: calculationAsOfDate,
      parAmount: amount.currentAmount,
    });
    const originalCost =
      isParValuedSecurityCategory(category) ||
      category === "slgs_time" ||
      category === "slgs_demand"
        ? amount.currentAmount
        : calculateOriginalCost(position, amount.currentAmount);
    const principalMarketValue = calculateMarketValue({
      position,
      security: security || {},
      valuation: amount.latestValuation,
      securityValuation,
      parAmount: amount.currentAmount,
    });
    const accruedInterest = isEffectiveYieldSecurityCategory(category)
      ? accruedInterestComponents?.accruedInterest ?? null
        : calculateAccruedInterest({
          position,
          security: security || {},
          valuation: amount.latestValuation,
          asOfDate: calculationAsOfDate,
          parAmount: amount.currentAmount,
          activities,
          treatCouponDateAsPrePayment: calculationAsOfDate !== asOfDate,
        });
    const marketValue = principalMarketValue;

    const isCusipPosition = isCusipInvestmentType(position);
    const positionPriceOverride = isCusipPosition
      ? null
      : toNumberOrNull(amount.latestValuation?.price);
    const securityPrice = toNumberOrNull(securityValuation?.price_mid);
    const displayPrice = isParValuedSecurityCategory(category)
      ? 100
      : isCusipPosition
      ? toNumberOrNull(amount.latestValuation?.price) ?? securityPrice
      : positionPriceOverride ?? securityPrice;
    const yields = computePositionYields({
      purchasePrice: toNumberOrNull(position.purchase_price),
      settlementDate: trimToEmpty(position.settlement_date || position.purchase_date),
      couponType: trimToEmpty(security?.coupon_type),
      couponRate: normalizeCouponRate(coupon),
      maturityDate,
      paymentFrequency: security?.payment_frequency,
      dayCountBasis: trimToEmpty(security?.day_count_basis) || "30/360",
      isCallable: Boolean(security?.is_callable),
      nextCallDate,
    });

    const daysToMaturity = daysBetween(calculationAsOfDate, maturityDate);
    const daysToCall = nextCallDate
      ? daysBetween(calculationAsOfDate, nextCallDate)
      : null;
    const cdEffectiveYield = category === "cd" ? calculateCdEffectiveYield(position) : null;
    const resolvedYtm = isEffectiveYieldSecurityCategory(category)
      ? effectiveYield
      : category === "cd"
      ? cdEffectiveYield
      : yields.ytm;
    const resolvedYtc = isEffectiveYieldSecurityCategory(category)
      ? null
      : yields.ytc;
    const resolvedYtw = isEffectiveYieldSecurityCategory(category)
      ? null
      : resolvedYtm == null
      ? resolvedYtc
      : resolvedYtc == null
      ? resolvedYtm
      : Math.min(resolvedYtm, resolvedYtc);

    rows.push({
      position,
      fund,
      portfolio,
      security,
      securityValuation,
      category,
      asOfDate,
      is_pending_settlement: isPendingSettlement,
      par_amount: isPendingSettlement ? 0 : amount.parAmount,
      amount: isPendingSettlement ? 0 : amount.currentAmount,
      current_amount: isPendingSettlement ? 0 : amount.currentAmount,
      units: isPendingSettlement ? 0 : amount.units,
      balance_amount: isPendingSettlement ? 0 : amount.balanceAmount,
      original_cost: isPendingSettlement ? 0 : originalCost,
      book_value: isPendingSettlement ? 0 : bookValue,
      principal_market_value: isPendingSettlement ? 0 : principalMarketValue,
      market_value: isPendingSettlement ? 0 : marketValue,
      accrued_interest: isPendingSettlement ? 0 : accruedInterest,
      market_value_plus_accrued:
        isPendingSettlement
          ? 0
          : marketValue == null
          ? null
          : marketValue + (accruedInterest ?? 0),
      unrealized_gain_loss:
        isPendingSettlement
          ? 0
          : principalMarketValue != null && originalCost != null
          ? principalMarketValue - originalCost
          : null,
      coupon,
      maturity_date: maturityDate,
      next_call_date: nextCallDate,
      effective_maturity_date: effectiveMaturityDate,
      days_to_maturity: daysToMaturity,
      days_to_call: daysToCall,
      days_to_effective_maturity: daysBetween(
        calculationAsOfDate,
        effectiveMaturityDate
      ),
      years_to_effective_maturity:
        daysBetween(calculationAsOfDate, effectiveMaturityDate) == null
          ? null
          : daysBetween(calculationAsOfDate, effectiveMaturityDate) / 365.25,
      yield_ytm_mid: resolvedYtm,
      yield_ytm_label: category === "cd"
        ? "Effective Yield"
        : isEffectiveYieldSecurityCategory(category)
        ? effectiveYieldOverride
          ? "Effective Yield Override"
          : "Effective Yield"
        : "YTM",
      effective_yield: category === "cd" ? cdEffectiveYield : effectiveYield,
      calculated_effective_yield: calculatedEffectiveYield,
      effective_yield_is_override: Boolean(effectiveYieldOverride),
      effective_yield_override_start_date:
        effectiveYieldOverride?.effective_yield_start_date || "",
      effective_yield_override_end_date:
        effectiveYieldOverride?.effective_yield_end_date || "",
      yield_ytc_mid: resolvedYtc,
      yield_ytw_mid: resolvedYtw,
      price_mid: displayPrice,
      latest_valuation: amount.latestValuation,
      latest_rating_valuation: ratingValuation,
    });
  }

  return { asOfDate, calculationAsOfDate, stateDateExclusive, rows };
}

function sumActivities(rows, types, valueFields) {
  return rows
    .filter((row) => types.includes(row.activity_type))
    .filter(
      (row) =>
        !(
          row.activity_type === "deposit" &&
          [
            "manual_interest_reinvestment",
            "system_scheduled_interest_reinvestment",
          ].includes(trimToEmpty(row.source_type))
        )
    )
    .reduce((sum, row) => {
      for (const field of valueFields) {
        const n = toNumberOrNull(row[field]);
        if (n != null) return sum + Math.abs(n);
      }
      return sum;
    }, 0);
}

function normalizeCdDayCountBasis(value) {
  const basis = trimToEmpty(value).toUpperCase();
  return ["ACT/365", "ACT/360", "30/360"].includes(basis)
    ? basis
    : "ACT/365";
}

function normalizeCdInterestFrequency(value) {
  const frequency = trimToEmpty(value).toLowerCase().replace(/\s+/g, "_");
  return ["weekly", "monthly", "quarterly", "semiannual", "annual", "at_maturity"].includes(frequency)
    ? frequency
    : "at_maturity";
}

function cdPaymentDates(settlementDate, maturityDate, frequency) {
  const settlement = parseIsoDate(settlementDate);
  const maturity = parseIsoDate(maturityDate);
  if (!settlement || !maturity || !maturity.isAfter(settlement, "day")) return [];
  if (frequency === "at_maturity") return [maturity.format("YYYY-MM-DD")];

  const interval = frequency === "weekly"
    ? { count: 1, unit: "week" }
    : frequency === "monthly"
    ? { count: 1, unit: "month" }
    : frequency === "quarterly"
    ? { count: 3, unit: "month" }
    : frequency === "semiannual"
    ? { count: 6, unit: "month" }
    : { count: 1, unit: "year" };
  const isMonthEnd = settlement.date() === settlement.endOf("month").date();
  const dates = [];
  for (let n = 1; n <= 6000; n += 1) {
    let date = settlement.add(interval.count * n, interval.unit);
    if (isMonthEnd && interval.unit !== "week") date = date.endOf("month");
    if (!date.isBefore(maturity, "day")) break;
    dates.push(date.format("YYYY-MM-DD"));
  }
  dates.push(maturity.format("YYYY-MM-DD"));
  return dates;
}

function calculateCdEffectiveYield(position = {}) {
  const settlementDate = trimToEmpty(position.settlement_date || position.purchase_date);
  const maturityDate = trimToEmpty(position.raw_maturity_date);
  const settlement = parseIsoDate(settlementDate);
  const maturity = parseIsoDate(maturityDate);
  const rate = normalizeCouponRate(position.raw_coupon);
  if (!settlement || !maturity || rate == null || !maturity.isAfter(settlement, "day")) return null;

  const horizon = maturity.isBefore(settlement.add(1, "year"), "day")
    ? maturity
    : settlement.add(1, "year");
  const horizonDate = horizon.format("YYYY-MM-DD");
  const basis = normalizeCdDayCountBasis(position.day_count_basis);
  const frequency = normalizeCdInterestFrequency(position.interest_frequency);
  let balance = 1;
  let periodStart = settlementDate;
  for (const paymentDate of cdPaymentDates(settlementDate, maturityDate, frequency)) {
    if (paymentDate > horizonDate) break;
    balance += balance * rate * dayCountFraction(periodStart, paymentDate, basis);
    periodStart = paymentDate;
  }
  if (periodStart < horizonDate) {
    balance += balance * rate * dayCountFraction(periodStart, horizonDate, basis);
  }
  const elapsedDays = horizon.diff(settlement, "day");
  return elapsedDays > 0 ? Math.pow(balance, 365 / elapsedDays) - 1 : null;
}

export function calculatePeriodInterestIncome({
  position = {},
  receivedInterest = 0,
  beginningRow = null,
  endingRow = null,
  startDate = "",
  endDate = "",
} = {}) {
  const received = toNumberOrZero(receivedInterest);
  if (!isCusipInvestmentType(position)) return received;

  const settlementDate = trimToEmpty(
    position?.settlement_date || position?.purchase_date
  );
  const purchaseFallsInPeriod = settlementDate && startDate && endDate
    ? settlementDate >= startDate && settlementDate <= endDate
    : !beginningRow && !!endingRow;
  const purchasedAccruedInterest = purchaseFallsInPeriod
    ? toNumberOrZero(position?.purchase_accrued_interest)
    : 0;

  return (
    received +
    toNumberOrZero(endingRow?.accrued_interest) -
    toNumberOrZero(beginningRow?.accrued_interest) -
    purchasedAccruedInterest
  );
}

export function buildPortfolioPeriodAnalysis({
  startDate,
  endDate,
  portfolios = [],
  funds = [],
  positions = [],
  activities = [],
  valuations = [],
  securities = [],
  securityValuations = [],
}) {
  const comparisonPeriod = getPortfolioComparisonPeriod(startDate, endDate);
  const beginningAsOfDate = addDaysIso(startDate, -1);
  const endingCalculationAsOfDate = addDaysIso(endDate, 1);
  const beginningStateDateExclusive = startDate;
  const endingStateDateExclusive = addDaysIso(endDate, 1);
  const beginning = buildPortfolioSnapshot({
    asOfDate: beginningAsOfDate,
    calculationAsOfDate: startDate,
    stateDateExclusive: beginningStateDateExclusive,
    portfolios,
    funds,
    positions,
    activities,
    valuations,
    securities,
    securityValuations,
  });
  const comparison = comparisonPeriod
    ? buildPortfolioSnapshot({
        asOfDate: comparisonPeriod.endDate,
        calculationAsOfDate: startDate,
        stateDateExclusive: startDate,
        yieldStartDate: comparisonPeriod.startDate,
        yieldEndDate: comparisonPeriod.endDate,
        portfolios,
        funds,
        positions,
        activities,
        valuations,
        securities,
        securityValuations,
      })
    : null;
  const ending = buildPortfolioSnapshot({
    asOfDate: endDate,
    calculationAsOfDate: endingCalculationAsOfDate,
    stateDateExclusive: endingStateDateExclusive,
    yieldStartDate: startDate,
    yieldEndDate: endDate,
    portfolios,
    funds,
    positions,
    activities,
    valuations,
    securities,
    securityValuations,
  });

  const periodActivities = activities.filter((row) =>
    dateInPeriod(row.activity_date, startDate, endDate)
  );
  const endingByPosition = new Map(
    ending.rows.map((row) => [row.position?.id, row])
  );
  const beginningByPosition = new Map(
    beginning.rows.map((row) => [row.position?.id, row])
  );

  const beginningValue = beginning.rows.reduce(
    (sum, row) => sum + toNumberOrZero(row.market_value_plus_accrued),
    0
  );
  const endingValue = ending.rows.reduce(
    (sum, row) => sum + toNumberOrZero(row.market_value_plus_accrued),
    0
  );
  const unrealizedGainLossChange =
    ending.rows.reduce((sum, row) => sum + toNumberOrZero(row.unrealized_gain_loss), 0) -
    beginning.rows.reduce((sum, row) => sum + toNumberOrZero(row.unrealized_gain_loss), 0);

  const activitiesByPositionId = new Map();
  const positionIds = new Set(positions.map((position) => position.id));
  for (const activity of periodActivities) {
    const positionId = activity?.position_id;
    if (!positionId) continue;
    if (!activitiesByPositionId.has(positionId)) activitiesByPositionId.set(positionId, []);
    activitiesByPositionId.get(positionId).push(activity);
  }

  const positionSummaries = positions.map((position) => {
    const endRow = endingByPosition.get(position.id);
    const beginRow = beginningByPosition.get(position.id);
    const isPendingAtPeriodEnd = Boolean(endRow?.is_pending_settlement);
    const security = endRow?.security || beginRow?.security || {};
    const amount =
      endRow?.par_amount ?? beginRow?.par_amount ?? toNumberOrNull(position.par_amount);
    const amortizationAccretion = calculateAmortizationAccretion({
      position,
      security,
      startDate,
      endDate: endingCalculationAsOfDate,
      parAmount: amount,
    });
    const positionActivities = activitiesByPositionId.get(position.id) || [];
    const receivedInterest = sumActivities(
      positionActivities,
      ["interest_income"],
      ["income_amount", "cash_amount"]
    );
    const couponIncome = calculatePeriodInterestIncome({
      position,
      receivedInterest,
      beginningRow: beginRow,
      endingRow: endRow,
      startDate,
      endDate,
    });
    const dividendIncome = sumActivities(
      positionActivities,
      ["dividend_income"],
      ["income_amount", "cash_amount"]
    );
    const realizedGainLoss = positionActivities.reduce(
      (sum, row) => sum + toNumberOrZero(row.realized_gain_loss),
      0
    );
    const normalizedAmortizationAccretion = toNumberOrZero(amortizationAccretion);

    return {
      positionId: position.id,
      receivedInterest: isPendingAtPeriodEnd ? 0 : receivedInterest,
      couponIncome: isPendingAtPeriodEnd ? 0 : couponIncome,
      dividendIncome: isPendingAtPeriodEnd ? 0 : dividendIncome,
      amortizationAccretion: isPendingAtPeriodEnd
        ? 0
        : normalizedAmortizationAccretion,
      realizedGainLoss: isPendingAtPeriodEnd ? 0 : realizedGainLoss,
      netIncome: isPendingAtPeriodEnd
        ? 0
        :
        couponIncome +
        dividendIncome +
        normalizedAmortizationAccretion +
        realizedGainLoss,
    };
  });
  const amortizationAccretion = positionSummaries.reduce(
    (sum, positionSummary) => sum + positionSummary.amortizationAccretion,
    0
  );
  const couponIncome = positionSummaries.reduce(
    (sum, positionSummary) => sum + positionSummary.couponIncome,
    0
  ) + sumActivities(
    periodActivities.filter(
      (activity) => !positionIds.has(activity?.position_id)
    ),
    ["interest_income"],
    ["income_amount", "cash_amount"]
  );

  return {
    startDate,
    endDate,
    beginningAsOfDate,
    beginningCalculationAsOfDate: startDate,
    endingCalculationAsOfDate,
    beginningStateDateExclusive,
    endingStateDateExclusive,
    beginning,
    comparison,
    comparisonPeriod,
    ending,
    activities: periodActivities,
    positionSummaries,
    summary: {
      beginningValue,
      purchases: sumActivities(periodActivities, ["purchase"], ["cash_amount"]),
      sales: sumActivities(periodActivities, ["sale", "partial_sale"], ["cash_amount"]),
      maturities: sumActivities(
        periodActivities,
        ["redemption", "principal_paydown"],
        ["cash_amount", "par_delta"]
      ),
      deposits: sumActivities(periodActivities, ["deposit"], ["cash_amount", "units_delta"]),
      withdrawals: sumActivities(periodActivities, ["withdrawal"], ["cash_amount", "units_delta"]),
      feeExpenses: sumActivities(periodActivities, ["fee_expense"], ["cash_amount", "units_delta"]),
      couponIncome,
      dividendIncome: sumActivities(periodActivities, ["dividend_income"], ["income_amount", "cash_amount"]),
      amortizationAccretion,
      realizedGainLoss: periodActivities.reduce(
        (sum, row) => sum + toNumberOrZero(row.realized_gain_loss),
        0
      ),
      unrealizedGainLossChange,
      endingValue,
    },
  };
}

export function calculateProjectedCashFlows({
  position = {},
  security = {},
  startDate,
  endDate,
  parAmount = null,
}) {
  const category = classifySecurity(position, security);
  const maturityDate = getStaticMaturityDate(position, security);
  const par = parAmount ?? toNumberOrNull(position.par_amount);
  const coupon = normalizeCouponRate(getStaticCoupon(position, security));
  const frequency = normalizeFrequency(security.payment_frequency || "semiannual");
  const dayCount = getCouponDayCountBasis(category, security);
  const flags = [];
  const flows = [];

  if (isCashLike(position)) {
    flags.push("Cash-like instruments do not have reliable projected principal/coupon schedules.");
    return { flows, flags };
  }

  if (!maturityDate) flags.push("Missing maturity date.");
  if (par == null) flags.push("Missing par amount.");
  if (!isDiscountSecurity(position, security) && coupon == null) {
    flags.push("Missing coupon.");
  }

  if (flags.length) return { flows, flags };

  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  const maturity = parseIsoDate(maturityDate);
  if (!start || !end || !maturity) return { flows, flags: ["Invalid cash flow date range."] };

  if (!isDiscountSecurity(position, security)) {
    const months = 12 / frequency;
    const couponDates = [];
    const firstInterest = parseIsoDate(security?.first_interest_payment_date);

    if (firstInterest && !firstInterest.isAfter(maturity, "day")) {
      for (
        let i = 0, payDate = firstInterest;
        i < 600 && !payDate.isAfter(maturity, "day");
        i += 1, payDate = payDate.add(months, "month")
      ) {
        couponDates.push(payDate);
      }
      if (!couponDates.some((date) => date.isSame(maturity, "day"))) {
        couponDates.push(maturity);
      }
    } else {
      for (
        let i = 0, payDate = maturity;
        i < 600 && payDate.isAfter(start, "day");
        i += 1, payDate = payDate.subtract(months, "month")
      ) {
        couponDates.push(payDate);
      }
    }

    couponDates
      .filter((date) => date.isAfter(start, "day") && !date.isAfter(end, "day"))
      .sort((a, b) => a.valueOf() - b.valueOf())
      .forEach((date) => {
        const prev = date.subtract(12 / frequency, "month");
        const prevDate = prev.format("YYYY-MM-DD");
        const couponDate = date.format("YYYY-MM-DD");
        const accrualStart = getCouponAccrualStartDate(
          security,
          prevDate,
          couponDate,
          position?.settlement_date || position?.purchase_date
        );
        const accrual = dayCountCouponPeriodFraction({
          start: accrualStart,
          end: couponDate,
          nextCoupon: couponDate,
          method: dayCount,
          frequency,
        });
        flows.push({
          date: couponDate,
          type: "coupon",
          amount: par * coupon * accrual,
          position_id: position.id,
        });
      });
  }

  if (maturity.isAfter(start, "day") && !maturity.isAfter(end, "day")) {
    flows.push({
      date: maturity.format("YYYY-MM-DD"),
      type: "principal",
      amount: par,
      position_id: position.id,
    });
  }

  return {
    flows: flows.sort((a, b) => trimToEmpty(a.date).localeCompare(trimToEmpty(b.date))),
    flags,
  };
}
